// Shared TypeScript types used by `@sss/api`, `@sss/sdk`, `@sss/web`, and
// `@sss/mobile`. Add new types here when they cross package boundaries; keep
// truly private types inside their owning package.
// --- Secrets (apps/worker /v1/secrets) ---
// The server only ever sees `payload` as opaque base64url ciphertext. The
// link key travels in the URL fragment and the sender token in the sender's
// own status link fragment; neither appears in these shapes except
// `senderToken`, which is returned once at creation.
/** Product limits for text secrets. The client checks them; the Worker enforces them. */
export const SECRET_LIMITS = {
    /** UTF-8 bytes of secret text (free tier). */
    maxTextBytes: 100 * 1024,
    minExpirySeconds: 5 * 60,
    maxExpirySeconds: 7 * 24 * 60 * 60,
    minViews: 1,
    maxViews: 10,
};
/**
 * Turnstile actions. The challenge page (apps/challenge) only renders these,
 * and the Worker checks a token's action matches the call it's used for, so
 * a token earned for one thing can't be spent on another.
 */
export const CHALLENGE_ACTIONS = {
    createSecret: 'create-secret',
    createRoom: 'create-room',
    buyPass: 'buy-pass',
    createDrop: 'create-drop',
};
// --- Live handoff rooms (apps/worker /v1/rooms, docs/adr/0010) ---
// A room pairs one sender and one recipient over WebSockets and passes
// sealed frames (docs/crypto-spec.md §5) between them. It never stores or
// reads them. The room key lives only in the link fragment.
export const ROOM_LIMITS = {
    /** How long a new link waits for the recipient: the sender's choices. */
    waitSeconds: [5 * 60, 15 * 60, 60 * 60],
    /** Once the recipient joins: time to receive, unlock and acknowledge. A file transfer keeps extending it (`active`). */
    handoffSeconds: 10 * 60,
    /** No room outlives this, however long a file transfer keeps it active. */
    maxLifetimeSeconds: 6 * 60 * 60,
    /** How long the room waits for a sender whose connection dropped. */
    senderGraceSeconds: 30,
    /** Largest relayed frame, in base64url characters (a 256 KiB record plus framing). */
    maxFrameChars: 350_000,
    /**
     * Total base64url characters a room will relay, both directions together:
     * one 25 MiB file transfer (RELAY_MAX_TOTAL_BYTES in apps/web) with its
     * records and signalling, as base64url (docs/adr/0012). Past it, the room ends.
     */
    maxRelayChars: 36_000_000,
};
// --- Paid relay (apps/worker /v1/passes, /v1/relay, docs/adr/0013) ---
// Files over 25 MB that can't connect directly go through Cloudflare's TURN
// relay, paid for with a pass. A pass is a bearer token (`sss_pass_…`) held
// in the sender's browser; the server keeps only its hash. A grant is one
// TURN credential, holding part of a pass's allowance until it's settled.
export const RELAY_LIMITS = {
    /** A grant's TURN credential lives as long as a room can (ROOM_LIMITS.maxLifetimeSeconds). */
    grantSeconds: 6 * 60 * 60,
    /** Grants a pass may have open at once. */
    maxOpenGrants: 3,
};
/**
 * The passes on sale (docs/adr/0013): what each holds, how many days it
 * stays valid, and its price, the same number in USD, EUR and GBP.
 */
export const PASS_OFFERS = {
    '10gb': { bytes: 10 * 1024 ** 3, days: 30, price: 2 },
    '50gb': { bytes: 50 * 1024 ** 3, days: 90, price: 8 },
    '200gb': { bytes: 200 * 1024 ** 3, days: 365, price: 30 },
};
export const PASS_SIZES = Object.keys(PASS_OFFERS);
/** What a request without a size buys: the pass that was the only one before sizes. */
export const DEFAULT_PASS_SIZE = '10gb';
// --- Stored file drops (apps/worker /v1/drops, docs/adr/0015) ---
// The server stores the drop's clear header, its sealed manifest and its
// sealed records (docs/crypto-spec.md §7). The link key never reaches it.
export const DROP_LIMITS = {
    maxDownloads: 10,
    /** The shortest and longest a sender may keep a drop. There is no product maximum; this one only bounds the arithmetic. */
    minKeepSeconds: 60 * 60,
    maxKeepSeconds: 3650 * 24 * 60 * 60,
    /** A started download may run (and resume) this long, even past the drop's end. */
    downloadWindowSeconds: 24 * 60 * 60,
    /** An upload that hasn't finished by then is abandoned. */
    uploadDeadlineSeconds: 24 * 60 * 60,
    /**
     * Records per upload part, at least: 6 MiB, above R2's 5 MiB minimum. Small
     * so a slow connection sees progress and loses little to a retry; larger
     * drops get larger parts (dropPartRecords) to stay under R2's 10,000.
     */
    partRecords: 6,
    /** R2's limit on parts in one multipart upload. */
    maxParts: 10_000,
    /** Largest Range a download may ask for at once. */
    maxRangeBytes: 16 * (2 ** 20 + 28),
};
/** Records per upload part for a drop of `totalRecords`: the minimum, or more to stay within R2's part limit. */
export const dropPartRecords = (totalRecords) => Math.max(DROP_LIMITS.partRecords, Math.ceil(totalRecords / DROP_LIMITS.maxParts));
/** What keeping data costs the pass (docs/adr/0015, confirmed by the owner 2026-10-31): 1 GB kept 30 days uses 1 GB. */
export const DROP_KEEP_RATE = { bytes: 1, seconds: 30 * 24 * 60 * 60 };
/**
 * The least time a finished drop is charged for, even if it's downloaded or
 * deleted sooner (owner's decision, 2026-11-01). R2 bills storage by each
 * day's peak, so a drop kept an hour costs us a full day; without this, a
 * drop uploaded and deleted at once would cost us its operations and a day of
 * storage while charging the pass next to nothing.
 */
export const DROP_MIN_CHARGE_SECONDS = 24 * 60 * 60;
/** What an abandoned upload's hold keeps, for the operations it used. */
export const DROP_ABANDON_CHARGE = 0.01;
